import { Box, Stack, Text } from '@chakra-ui/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import Introduction from './Introduction/Introduction.js';
import { getPersonalityBasicData } from '../../services/Personality.js';
import Economics from './EconomicsOfCinema/Economics.js';
import General from './General/General.js';
import MyTabsNested from '../../components/MyTabsNested.js';
import Basic from './General/Basic.js';
import Preface from './Introduction/Preface.js';
import Personalia from './Introduction/Personalia.js';
import Education from './Introduction/Education.js';
import TeachingInfra from './Introduction/TeachingInfra.js';
import Screenings from '../Films/EconomicsOfCinema/Screenings.js';
import Awards from '../Films/EconomicsOfCinema/Awards.js';
import { useSelector } from 'react-redux';
import Sections from '../../CommonComponents/Sections.js';
import MyAccordionNested from '../../components/MyAccordionNested.js';
import FlipBook from '../FlipBooks/FlipBook.js';
import { DataNotFound } from '../../CommonComponents/DataNotFound.js';
import IntegratedTimeline from './IntegratedTimeline/IntegratedTimeline.js';
import BodyOfWork from './BodyOfWork/BodyOfWork.js';
import Exhibitions from './BodyOfWork/Exhibitions.js';
import ResearchCentre from '../../FunctionalComponents/ResearchCentre/ResearchCentre.js';
import Archive from '../../FunctionalComponents/ResearchCentre/Archive.js';
import Library from '../../FunctionalComponents/ResearchCentre/Library.js';
import { getFlipBookData } from '../../services/FlipBooks.js';

const PersonalityEdit = () => {

  const { alias } = useParams();
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const personalityType = pathParts?.[1];
  const [masterlistType, setMasterlistType] = useState(null);
  const navigate = useNavigate();
  const [filmTitle, setFilmTitle] = useState();
  const [hasFlipBooks, setHasFlipBooks] = useState(false);
  const previewEnabled = useSelector(state => state.preview.isEnabled);  
  const isPhotographer = masterlistType?.includes('Photographer');
  const isArtist = masterlistType?.includes('Artist');
  
  const tabs = useMemo(() => {
    let initialTabs = [
      {
        id: "general", title: "General", Component: General,
        subTabs: [
          { id: "primary", title: "Primary", Component: Basic },
          { id: "sections", title: "Sections", Component: Sections },
        ]
      },
      {
        id: "introduction", title: "Introduction", Component: Introduction,
        subTabs: [
          { id: "preface", title: "Preface", Component: Preface },
          { id: "personalia", title: "Personalia", Component: Personalia },
          { id: "education", title: "Education", Component: Education },
          { id: "teaching-infra", title: "Teaching & Infrastructure-Building", Component: TeachingInfra },
        ]
      },
      {
        id: "research-centre", title: "Research Centre", Component: ResearchCentre,
        subTabs: [
          { id: "library", title: "Library", Component: Library },
          { id: "archive", title: "Archive", Component: Archive },

        ],
      },
      {
        id: "economics-of-cinema", title: "Economics of Cinema", Component: Economics,
        subTabs: [
          { id: "screenings", title: "Screenings", Component: Screenings },
          { id: "awards-honours", title: "Awards and Honours", Component: Awards },
        ]
      },
      {
        id: "integrated-timeline", title: "Integrated Timeline", Component: IntegratedTimeline,
      }
    ];

    // Build Body of Work tab based on personality type and flip book data
    if (isPhotographer) {
      // For photographers, always show Body of Work with appropriate subtabs
      const subTabs = [
        { id: "photographs-publications", title: "Photographs Publications", Component: DataNotFound }
      ];
      
      // Add FlipBook subtab if flip book data exists
      if (hasFlipBooks) {
        subTabs.push({ id: "flip-books", title: "Flip Books", Component: FlipBook });
      }
      
      initialTabs.push({
        id: "body-of-work", 
        title: "Body Of Work", 
        Component: null, 
        subTabs: subTabs
      });
    } else if (isArtist) {
      // For artists, show Body of Work with Exhibitions only (no FlipBook)
      initialTabs.push({
        id: "body-of-work", 
        title: "Body Of Work", 
        Component: BodyOfWork,
        subTabs: [
          { id: "exhibitions", title: "Exhibitions", Component: Exhibitions }
        ]
      });
      
      // Replace Economics of Cinema with Economics of Arts for artists
      initialTabs = initialTabs.filter(tab => tab.id !== "economics-of-cinema");
      initialTabs.push({
        id: "economics-of-arts", 
        title: "Economics of Arts", 
        Component: Economics,
        subTabs: [
          { id: "awards-honours", title: "Awards and Honours", Component: Awards },
        ],
      });
    }
    // Note: FlipBook is only available for photographers, not for other personality types
    return initialTabs;
  }, [isPhotographer, isArtist, hasFlipBooks]);


  // Check if personality has flip book data
  useEffect(() => {
    const checkFlipBooks = async () => {
      try {
        if (alias) {
          const result = await getFlipBookData(alias);
          // Check if flip book data exists
          if (result?.data?.[0]?.images?.length > 0) {
            setHasFlipBooks(true);
          } else {
            setHasFlipBooks(false);
          }
        }
      } catch (error) {
        console.error('Error checking flip book data:', error);
        setHasFlipBooks(false);
      }
    };

    checkFlipBooks();
  }, [alias]);

  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getPersonalityBasicData(alias);

          if (result) {
            setFilmTitle(result?.full_name);
            setMasterlistType(result?.masterlist_type);
          }
        }
      } catch (error) {
        console.error('Error fetching film data:', error);
      }
    };

    getBasicData();
  }, [alias]);

  // Redirect to default tab if no tab is specified
  useEffect(() => {
    if (pathParts?.length === 3 && alias) {
      // Redirect to the default tab/subTab
      navigate(`/${personalityType}/${alias}/agraphy/${tabs[0].id}/${tabs[0].subTabs[0].id}`, { replace: true });
    }
  }, [location.pathname, alias, navigate, tabs, pathParts, personalityType]);

  return (
    <Stack px="1rem" pb={'1rem'} width={"100%"}>
    <Box>
      <Text fontSize={"18px"} pt={2} fontWeight={"600"}>{filmTitle}</Text>
    </Box>
      {previewEnabled ?
        <MyAccordionNested orientation="vertical" list={tabs} urlBasePath={`/${personalityType}`} tabSegmentPosition={3} subTabSegmentPosition={4} includeAgraphy /> :
        <MyTabsNested orientation="vertical" list={tabs} urlBasePath={`/${personalityType}`} tabSegmentPosition={3} subTabSegmentPosition={4} includeAgraphy />}
    </Stack>
  );
};

export default PersonalityEdit;